import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { Modal } from "~/components/AccountActions/modal.web";
import { useCurrentSpace } from "~/hooks/useCurrentSpace";
import { generateObjectId } from "~/lib/utils";
import { useAccountStore } from "~/stores/space";
import { Tag } from "~/stores/space/store";
import { Button } from "~/ui/Button";
import { Form } from "~/ui/Form";
import { TextInput } from "~/ui/TextInput";

const schema = z.object({
  id: z.string().optional(),
  value: z
    .string()
    .trim()
    .min(1, "Tag is required.")
    .max(50, "Tag must be less than 50 characters."),
});

export function TagDialog(props: { onDismiss: () => void; tag?: Tag }) {
  const { control, handleSubmit } = useForm({
    resolver: zodResolver(schema),
    defaultValues: props.tag ?? {
      value: "",
    },
  });
  const { store } = useAccountStore();

  const { space } = useCurrentSpace();

  const submitForm = (data: { value: string }) => {
    if (props.tag) {
      store.updateTag({
        set: {
          value: data.value,
        },
        tagId: props.tag.id,
        spaceId: space.id,
      });

      props.onDismiss();
      return;
    }

    data.value
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean)
      .forEach((value) => {
        store.createTag({
          set: {
            id: generateObjectId(),
            value: value.trim(),
          },
          spaceId: space.id,
        });
      });

    props.onDismiss();
  };

  return (
    <Modal
      onDismiss={props.onDismiss}
      header={props.tag ? `Edit tag` : "Create tag"}
    >
      <Form onSubmit={handleSubmit(submitForm)} className="gap-4">
        <Controller
          control={control}
          name="value"
          render={({ field: { onChange, value }, fieldState }) => (
            <TextInput
              autoFocus
              value={value}
              onChangeText={onChange}
              placeholder="foo"
              label="Tag(s)"
              hint="Comma (,) separated to create multiple at once"
              errorMessage={fieldState.error?.message}
            />
          )}
        />
        <Button onPress={handleSubmit(submitForm)}>
          {props.tag ? "Edit tag" : "Create tag"}
        </Button>
      </Form>
    </Modal>
  );
}
